(ns app.bulk.page
  "Replicant page for bulk scheduling Instagram Stories.
   Accessible at `/ui/bulk`."
  (:require [clojure.string :as str]
            [parts.replicant.query :as query]
            [storrito.date.datetime-local :as datetime-local]
            [storrito.state.local-storage :as local-storage]
            [ui.account-selector :as account-selector]
            [ui.app-frame :as app-frame]))

(defn init-query
  "The query without a user, for the list of Instagram users."
  [state]
  {:query/kind :query/bulk
   :query/data {:timezone-offset-ms (::timezone-offset-ms state)
                :time-zone-id (::time-zone-id state)}})

(defn bulk-query
  [state]
  (let [ig-user-id (::selected-ig-user-id state)]
    (cond-> (init-query state)
      ig-user-id
      (assoc-in [:query/data :instagram.user/id] ig-user-id))))

(defn- normalize-row
  "Canonicalizes a row for change comparison: drops entries whose
   value is nil, false or an empty string. The query emits unset
   attributes as explicit nil/false keys while the UI controls
   produce dissoc'ed keys (FB toggle), \"\" (highlight select) and
   false (checkboxes) — without this, toggling a value on and back
   off would leave the row 'edited' forever and save would
   pointlessly cancel and re-create it."
  [row]
  (into {}
        (remove (fn [[_ v]]
                  (or (nil? v)
                      (false? v)
                      (= "" v))))
        row))

(defn- rows-differ?
  "True when the two rows differ in canonical form (see
   `normalize-row`). Every change comparison — row markers, pending
   counts, save ops, validation — goes through this, so they can
   never disagree with each other."
  [row-a row-b]
  (not= (normalize-row row-a)
        (normalize-row row-b)))

(defn- unsaved-changes?
  [rows loaded-rows]
  (and rows loaded-rows
       (not= (mapv normalize-row (sort-by (comp str :story.post/eid) rows))
             (mapv normalize-row (sort-by (comp str :story.post/eid) loaded-rows)))))

(defn parse-date-str
  "Converts 'yyyy-MM-dd HH:mm' to a js/Date."
  [date-str]
  (when (seq date-str)
    (let [parts (re-matches #"(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})" date-str)]
      (when parts
        (let [[_ y m d h min] parts]
          (js/Date. (js/parseInt y) (dec (js/parseInt m)) (js/parseInt d)
                    (js/parseInt h) (js/parseInt min)))))))

(defn format-row-date-str
  "Converts a js/Date to 'yyyy-MM-dd HH:mm' — the single formatter for
   the bulk row date format (also used by the gallery add in
   `app.bulk.actions`)."
  [date]
  (let [pad (fn [n]
              (if (< n 10) (str "0" n) (str n)))]
    (str (.getFullYear date) "-"
         (pad (inc (.getMonth date))) "-"
         (pad (.getDate date)) " "
         (pad (.getHours date)) ":"
         (pad (.getMinutes date)))))

(defn- shift-months!
  "Shifts the js/Date by `amount` months, clamping to the last day of
   the target month (May 31 + 1 month = Jun 30) like the common date
   libraries do, instead of letting js/Date overflow into the next
   month. Setting the day to 1 first keeps the intermediate setMonth
   from overflowing."
  [date amount]
  (let [day (.getDate date)]
    (.setDate date 1)
    (.setMonth date (+ (.getMonth date) amount))
    (.setDate date (min day
                        (.getDate (js/Date. (.getFullYear date)
                                            (inc (.getMonth date))
                                            0))))))

(defn shift-date-str
  "Shifts a 'yyyy-MM-dd HH:mm' string by `amount` `unit`s
   (:minutes/:hours/:days/:months); a negative amount shifts earlier.
   Uses js/Date field arithmetic in the browser timezone — the same
   interpretation `prepare-ops` uses when saving: a day or month shift
   keeps the wall-clock time across a DST change, month shifts clamp
   to the last day of the target month (May 31 + 1 month = Jun 30),
   and minute and hour shifts are plain arithmetic. Returns the input
   unchanged when it does not parse — or when the shifted date leaves
   the 'yyyy-MM-dd HH:mm' range (an absurd amount yields NaN fields
   or a 5-digit year, both rejected by the parse regex)."
  [date-str amount unit]
  (if-let [date (parse-date-str date-str)]
    (do
      (case unit
        :minutes (.setMinutes date (+ (.getMinutes date) amount))
        :hours (.setHours date (+ (.getHours date) amount))
        :days (.setDate date (+ (.getDate date) amount))
        :months (shift-months! date amount))
      (let [shifted (format-row-date-str date)]
        (if (parse-date-str shifted)
          shifted
          date-str)))
    date-str))

(defn- validate-date [date-str]
  (if-let [date (parse-date-str date-str)]
    (when (< (.getTime date)
             (datetime-local/current-minute-ms))
      "Date must be in the future")
    "Invalid date"))

(def same-minute-error
  ;; carries the one-click fix in the row's error line (see
  ;; `same-minute-fix-button`), so the message doubles as the
  ;; identity of this error kind:
  "Another story is scheduled for this minute")

(defn- validate-link [link]
  (when (and (seq link) (not (re-find #"^https?://" link)))
    "Must start with http:// or https://"))

(defn validate-rows
  "Validates only rows that differ from their loaded snapshot. Untouched
   rows produce no save ops (see `prepare-ops`), so a pre-existing
   overdue post — e.g. one that never published because the account was
   disconnected — must not block saving unrelated edits (the same goes
   for a pre-existing same-minute pair). A changed row sharing its
   minute with any other row is an error: the posting order within a
   minute is undefined, and the server silently skips a same-minute
   duplicate of the same story on save."
  [rows loaded-rows]
  (let [loaded (into {} (map (juxt :story.post/eid identity)) loaded-rows)
        minute-counts (frequencies (map :story.post/date-str rows))]
    (into {}
          (keep (fn [row]
                  (when (rows-differ? row (loaded (:story.post/eid row)))
                    (let [errors (into {}
                                       (remove (comp nil? val))
                                       {:date (or (validate-date (:story.post/date-str row))
                                                  (when (< 1 (get minute-counts
                                                                  (:story.post/date-str row)
                                                                  0))
                                                    same-minute-error))
                                        :link (validate-link (:story.post/link row))})]
                      (when (seq errors)
                        [(:story.post/eid row) errors])))))
          rows)))

(defn- prepare-ops
  "Computes cancel-ops and add-ops by diffing loaded-rows vs current rows."
  [rows loaded-rows]
  (let [loaded (into {} (map (juxt :story.post/eid identity)) loaded-rows)
        current (into {} (map (juxt :story.post/eid identity)) rows)
        removed (keep (fn [[eid _]] (when-not (current eid) eid)) loaded)
        changed (keep (fn [[eid row]]
                        (when-let [loaded-row (loaded eid)]
                          (when (rows-differ? row loaded-row) row)))
                      current)
        new-rows (keep (fn [[eid row]]
                         (when-not (loaded eid) row))
                       current)
        cancel-ops (concat removed (keep (fn [row] (when (loaded (:story.post/eid row))
                                                     (:story.post/eid row)))
                                         changed))
        add-ops (map (fn [row]
                       (cond-> (-> row
                                   (select-keys [:story.post/media
                                                 :instagram/user-id
                                                 :share-to-fb-id :share-to-fb-type])
                                   (assoc :story.post/date (parse-date-str (:story.post/date-str row)))
                                   (assoc :share-to-fb? (boolean (:share-to-fb? row)))
                                   (assoc :story.post/ai-generated? (boolean (:story.post/ai-generated? row))))
                         (not-empty (:story.post/link row))
                         (assoc :story.post/link (:story.post/link row))
                         (not-empty (:story.post/highlight-id row))
                         (assoc :story.post/highlight-id (:story.post/highlight-id row))))
                     (concat changed new-rows))]
    {:cancel-ops (vec cancel-ops)
     :add-ops (vec add-ops)}))

(defn- save-command [rows loaded-rows]
  {:command/kind :command/bulk-save
   :command/data (prepare-ops rows loaded-rows)})

(defn clear-selection
  "Drops the row selection, its shift-click anchor and the 'Move to'
   target override. The single helper for every path that invalidates
   the selection (account switch, discard, post-save refresh, toolbar
   button)."
  [state]
  (dissoc state ::selected-eids ::range-anchor ::move-to))

;; -- IG user selector --

(defn- ig-user-click-actions
  [state user-id]
  [[:bulk/maybe-discard
    [[:store/assoc ::selected-ig-user-id user-id]
     [:store/assoc ::rows nil]
     [:store/assoc ::loaded-rows nil]
     [:store/dissoc ::saving?]
     [:store/dissoc ::save-status]
     [:store/dissoc ::awaiting-refresh?]
     [:store/update clear-selection]
     [:ig/save-selection user-id]
     [:data/query {:query/kind :query/bulk
                   :query/data {:instagram.user/id user-id
                                :timezone-offset-ms (::timezone-offset-ms state)
                                :time-zone-id (::time-zone-id state)}}]]]])

;; -- Row editing --

(defn- current-rows
  "The rows the table shows: the edited rows, or the rows of the
   current query result before any edit session. The single source of
   truth for `render`, `start-editing` and the selection handlers."
  [state]
  (or (::rows state)
      (vec (:bulk/posts (query/get-result state (bulk-query state))))))

(defn start-editing
  "Initializes `::rows` and `::loaded-rows` together from the current
   query result, so both sides of the save diff refer to the rows the
   user is looking at. Without this snapshot the diff basis silently
   re-bases onto whatever the latest query result is: a refresh landing
   mid-edit (navigation, another save, posts created via the API) shifts
   `loaded-rows` under the user's feet and a save can cancel posts the
   user never touched."
  [state]
  (if (::rows state)
    state
    (let [posts (current-rows state)]
      (assoc state
             ::rows posts
             ::loaded-rows posts))))

(defn unsaved-changes-in-state?
  "Public predicate: true when bulk has unsaved local row edits compared
   to the snapshot they are based on. `::rows` and `::loaded-rows` are
   always set together by `start-editing`, so no edits means both are
   nil and the diff is empty."
  [state]
  (unsaved-changes? (::rows state) (::loaded-rows state)))

(defn- edit-rows
  "Applies `f` to the edited rows. No-op while the post-save refresh is
   in flight (`::awaiting-refresh?`), because edits made against the
   pre-refresh rows would reference story-post eids that the save just
   cancelled and re-created."
  [state f]
  (if (::awaiting-refresh? state)
    state
    (-> state
        (start-editing)
        (update ::rows f)
        (dissoc ::save-status))))

(defn- update-row [state eid k v]
  (edit-rows state
             (fn [rows]
               (mapv (fn [row]
                       (if (= (:story.post/eid row) eid)
                         (assoc row k v)
                         row))
                     rows))))

(defn- update-row-date [state eid k v]
  (update-row state eid k (str/replace v "T" " ")))

(defn- edit-rows-in
  "Applies `f` to every row whose eid is in `eids` (via `edit-rows`)."
  [state eids f]
  (edit-rows state
             (fn [rows]
               (mapv (fn [row]
                       (if (contains? eids (:story.post/eid row))
                         (f row)
                         row))
                     rows))))

(defn row-by-eid
  [state eid]
  (some #(when (= eid (:story.post/eid %)) %)
        (current-rows state)))

(defn- set-row-share-to-fb
  "Sets the FB cross-post flag on a single row. When enabling and the
   row has no linkage stored yet, defaults to `default-linkage` (the
   IG user's first FB linkage); when disabling, removes the linkage
   data."
  [row share? default-linkage]
  (if share?
    (let [id (or (:share-to-fb-id row)
                 (some-> default-linkage
                         :facebook-linkage/id
                         str))
          type (or (:share-to-fb-type row)
                   (:facebook-linkage/type default-linkage))]
      (cond-> (assoc row :share-to-fb? true)
        id   (assoc :share-to-fb-id id)
        type (assoc :share-to-fb-type type)))
    (-> row
        (assoc :share-to-fb? false)
        (dissoc :share-to-fb-id :share-to-fb-type))))

(defn- set-all-share-to-fb
  "Enables or disables the FB cross-post flag on all rows at once."
  [state share? default-linkage]
  (edit-rows state
             (fn [rows]
               (mapv #(set-row-share-to-fb % share? default-linkage)
                     rows))))

(defn- set-all-rows
  "Sets `k` to `v` on all rows at once."
  [state k v]
  (edit-rows state
             (fn [rows]
               (mapv (fn [row]
                       (assoc row k v))
                     rows))))

(defn- remove-row
  "Removes the row and drops it from the shift selection; the range
   anchor is cleared when it pointed at the removed row. A full no-op
   while the post-save refresh is in flight, so a blocked removal
   does not uncheck the row it failed to remove."
  [state eid]
  (if (::awaiting-refresh? state)
    state
    (cond-> (-> (edit-rows state
                           (fn [rows]
                             (vec (remove #(= (:story.post/eid %) eid) rows))))
                (update ::selected-eids (fnil disj #{}) eid))
      (= eid (::range-anchor state))
      (dissoc ::range-anchor))))

;; -- Desktop table --

(defn- sort-indicator [dir]
  (case dir
    :asc  " ▲"
    :desc " ▼"
    ""))

(defn- sorted-rows
  "The display order behind the Post date header sort. `::rows` is
   kept in ascending date order (see `rows-in-date-order`), so the
   sorted views are the stored order and its reverse. Deliberately
   not a sort by the live date value: that would move a row on every
   keystroke of a hand-edited date, and since the table rows are
   keyed by position (see `table-row`) the remaining keystrokes would
   land in the neighbouring story's input. The stored order only
   changes once the edit is committed (`resort-rows`)."
  [rows dir]
  (if (= dir :desc)
    (vec (reverse rows))
    rows))

(defn rows-in-date-order
  "Re-establishes the stored-rows invariant: `::rows` is kept in
   ascending date order, since the unsorted table view renders it as
   stored. Every operation that changes dates or adds rows (shift,
   drag-reorder, gallery add) must go through this."
  [rows]
  (vec (sort-by :story.post/date-str rows)))

(defn resort-rows
  "Re-establishes the ascending stored date order after a hand-edited
   post date — wired to the date inputs' blur event, so it only runs
   once the user has left the input. Not the change event: Chrome
   fires change on a datetime-local input for every completed segment
   edit (hour, minute), while it is still focused. The table rows are
   keyed by position (see `table-row`), so a re-sort mid-typing swaps
   the row contents under the focused input and the remaining
   keystrokes land in the NEXT story's date."
  [state]
  (edit-rows state rows-in-date-order))

(defn fix-same-minute
  "Moves the row with `eid` to the next free minute after its current
   one — the one-click fix behind the same-minute validation error.
   Scans forward past occupied minutes; a date that cannot be bumped
   (unparseable, overflow) stays unchanged. Goes through `edit-rows`
   and re-sorts, so the row lands at its new slot and the refresh
   guard applies."
  [state eid]
  (edit-rows state
             (fn [rows]
               (let [taken (into #{}
                                 (comp (remove #(= eid (:story.post/eid %)))
                                       (map :story.post/date-str))
                                 rows)]
                 (rows-in-date-order
                   (mapv (fn [row]
                           (if (= eid (:story.post/eid row))
                             (update row :story.post/date-str
                                     (fn [date-str]
                                       (loop [candidate date-str]
                                         (let [next-candidate (shift-date-str candidate 1 :minutes)]
                                           (cond
                                             (= next-candidate candidate) date-str
                                             (contains? taken next-candidate) (recur next-candidate)
                                             :else next-candidate)))))
                             row))
                         rows))))))

(defn move-row
  "Moves the element at `from-idx` to `to-idx`."
  [rows from-idx to-idx]
  (let [row (nth rows from-idx)
        without (vec (concat (subvec rows 0 from-idx)
                             (subvec rows (inc from-idx))))]
    (vec (concat (subvec without 0 to-idx)
                 [row]
                 (subvec without to-idx)))))

(defn- display-index-of
  [display-rows eid]
  (when eid
    (first (keep-indexed
             (fn [idx row]
               (when (= (:story.post/eid row) eid)
                 idx))
             display-rows))))

(defn selected-in-rows
  "The subset of `selected-eids` that still identifies a row in `rows`:
   the selection can go stale when rows are removed or replaced by a
   refresh, and stale eids must not count anywhere."
  [selected-eids rows]
  (into #{}
        (comp (map :story.post/eid)
              (filter (or selected-eids #{})))
        rows))

(defn- range-eids
  "The eids of the display-order rows between the anchor and the
   clicked row (both inclusive), or nil when either is not in the
   current display order (stale anchor, removed row)."
  [state anchor-eid eid]
  (let [display (vec (sorted-rows (current-rows state) (::sort-date state)))
        anchor-idx (display-index-of display anchor-eid)
        click-idx (display-index-of display eid)]
    (when (and anchor-idx click-idx)
      (into #{}
            (map :story.post/eid)
            (subvec display
                    (min anchor-idx click-idx)
                    (inc (max anchor-idx click-idx)))))))

(defn row-select-click
  "Handles a click on a row selection checkbox. A plain click toggles
   the row and remembers it as the anchor for a subsequent shift-click,
   which applies the clicked row's toggle to all rows between the
   anchor and the clicked row in the current display order:
   shift-clicking an unselected row selects the range, shift-clicking
   a selected row deselects it. The anchor stays unchanged, so
   consecutive shift-clicks extend from the same anchor. A shift-click
   without a resolvable anchor falls back to a plain toggle."
  [state eid shift?]
  (let [eids (when shift?
               (when-let [anchor (::range-anchor state)]
                 (range-eids state anchor eid)))
        selected (or (::selected-eids state) #{})]
    (if eids
      (assoc state ::selected-eids
             (if (contains? selected eid)
               (apply disj selected eids)
               (into selected eids)))
      (-> state
          (assoc ::selected-eids
                 (if (contains? selected eid)
                   (disj selected eid)
                   (conj selected eid)))
          (assoc ::range-anchor eid)))))

(defn fb-click
  "Handles a click on a row's FB cross-post checkbox. A plain click
   toggles the flag and remembers the row as the FB anchor; a
   shift-click applies the clicked row's new state to all rows between
   the anchor and the clicked row in the current display order — like
   the selection checkboxes. A shift-click without a resolvable anchor
   falls back to a plain toggle; a stale anchor (removed row, post-save
   refresh) resolves to nothing and falls back the same way."
  [state eid default-linkage shift?]
  (let [share? (not (:share-to-fb? (row-by-eid state eid)))
        eids (when shift?
               (when-let [anchor (::fb-range-anchor state)]
                 (range-eids state anchor eid)))]
    (if eids
      (edit-rows-in state eids
                    #(set-row-share-to-fb % share? default-linkage))
      (-> (edit-rows-in state #{eid}
                        #(set-row-share-to-fb % share? default-linkage))
          (assoc ::fb-range-anchor eid)))))

(defn ai-click
  "Like `fb-click` for the AI-generated label, with its own anchor."
  [state eid shift?]
  (let [value (not (:story.post/ai-generated? (row-by-eid state eid)))
        eids (when shift?
               (when-let [anchor (::ai-range-anchor state)]
                 (range-eids state anchor eid)))]
    (if eids
      (edit-rows-in state eids
                    #(assoc % :story.post/ai-generated? value))
      (-> (edit-rows-in state #{eid}
                        #(assoc % :story.post/ai-generated? value))
          (assoc ::ai-range-anchor eid)))))

(defn set-all-selected
  "Selects or deselects all currently shown rows at once."
  [state select?]
  (-> state
      (assoc ::selected-eids
             (if select?
               (into #{}
                     (map :story.post/eid)
                     (current-rows state))
               #{}))
      (dissoc ::range-anchor)))

(defn- parse-shift-amount
  "The shift amount as a positive integer up to 9999, or nil for
   anything else (empty input, zero, decimals, garbage, absurdly
   large values)."
  [s]
  (when (and (string? s)
             (re-matches #"\d+" (str/trim s)))
    (let [n (js/parseInt s 10)]
      (when (<= 1 n 9999)
        n))))

(defn shift-offset
  "Derives the shift offset `[amount unit]` (amount positive) from
   the toolbar widget state, or nil while the amount input does not
   hold a positive integer up to 9999. The direction comes from the
   clicked Earlier/Later button (see `apply-shift`)."
  [state]
  (when-let [amount (parse-shift-amount (::shift-amount state "30"))]
    [amount
     (::shift-unit state :minutes)]))

(defn apply-shift
  "Shifts the scheduled dates of the selected rows by the toolbar
   amount in `direction` (:earlier or :later) and re-stores the rows
   in ascending date order (the same invariant `reorder-rows` keeps).
   No-op while the offset is invalid or nothing is selected. The
   selection is kept, so repeated clicks nudge further and the
   opposite button undoes a wrong shift."
  [state direction]
  (let [offset (shift-offset state)
        selected (selected-in-rows (::selected-eids state)
                                   (current-rows state))]
    (if (and offset (seq selected))
      (let [[amount* unit] offset
            amount (if (= :earlier direction)
                     (- amount*)
                     amount*)]
        (edit-rows state
                   (fn [rows]
                     (rows-in-date-order
                       (mapv (fn [row]
                               (if (contains? selected (:story.post/eid row))
                                 (update row :story.post/date-str
                                         shift-date-str amount unit)
                                 row))
                             rows)))))
      state)))

(defn move-to-deltas
  "The [day-delta minute-delta] that moves `from-str` onto `to-str`
   (both 'yyyy-MM-dd HH:mm'): the calendar-day difference first, then
   the wall-clock minute-of-day difference. Applying both via
   `shift-date-str` lands `from-str` exactly on `to-str` and keeps
   the wall-clock spacing of the other moved rows across DST changes
   (a pure millisecond delta would drift them by an hour). Nil when
   either string does not parse."
  [from-str to-str]
  (let [from (parse-date-str from-str)
        to (parse-date-str to-str)]
    (when (and from to)
      [(js/Math.round
         (/ (- (.getTime (js/Date. (.getFullYear to)
                                   (.getMonth to)
                                   (.getDate to)))
               (.getTime (js/Date. (.getFullYear from)
                                   (.getMonth from)
                                   (.getDate from))))
            (* 24 60 60 1000)))
       (- (+ (* 60 (.getHours to)) (.getMinutes to))
          (+ (* 60 (.getHours from)) (.getMinutes from)))])))

(defn earliest-selected-date-str
  "The date-str of the earliest selected story — the anchor of the
   'Move to' tool (string sort works for the zero-padded format)."
  [state]
  (let [selected (selected-in-rows (::selected-eids state)
                                   (current-rows state))]
    (->> (current-rows state)
         (filter (comp selected :story.post/eid))
         (map :story.post/date-str)
         (sort)
         (first))))

(defn apply-move-to
  "Moves the earliest selected story onto the toolbar target datetime
   (`::move-to`, a datetime-local string; without one the picker
   default — the earliest itself — applies, a harmless no-op); the
   other selected stories follow, keeping their day and wall-clock
   spacing (see `move-to-deltas`). A successful move drops the target
   override, so the picker re-derives from the new earliest. The
   selection is kept, so a wrong move can be corrected right away.
   No-op while the target is invalid, nothing is selected or the
   post-save refresh is in flight."
  [state]
  (let [selected (selected-in-rows (::selected-eids state)
                                   (current-rows state))
        earliest (earliest-selected-date-str state)
        target-str (or (some-> (::move-to state)
                               (str/replace "T" " "))
                       earliest)
        deltas (when (and earliest target-str)
                 (move-to-deltas earliest target-str))]
    (if (and deltas
             (seq selected)
             (not (::awaiting-refresh? state)))
      (let [[day-delta minute-delta] deltas]
        (-> (edit-rows state
                       (fn [rows]
                         (rows-in-date-order
                           (mapv (fn [row]
                                   (if (contains? selected (:story.post/eid row))
                                     (update row :story.post/date-str
                                             (fn [date-str]
                                               (-> date-str
                                                   (shift-date-str day-delta :days)
                                                   (shift-date-str minute-delta :minutes))))
                                     row))
                                 rows))))
            (dissoc ::move-to)))
      state)))

(defn remove-selected-rows
  "Removes all selected rows from the edit buffer and clears the
   selection. Like the per-row Remove this is not destructive: the
   posts are only cancelled when the user saves, and Discard restores
   them. A full no-op while the post-save refresh is in flight or
   nothing (current) is selected."
  [state]
  (let [selected (selected-in-rows (::selected-eids state)
                                   (current-rows state))]
    (if (or (::awaiting-refresh? state)
            (empty? selected))
      state
      (-> (edit-rows state
                     (fn [rows]
                       (vec (remove (comp selected :story.post/eid)
                                    rows))))
          (clear-selection)))))

(defn toggle-tool
  "Activates a toolbar tool, or collapses the tool row when the active
   tool is clicked again. `::active-tool` defaults to :shift when the
   key is absent, so the common shift flow opens with zero extra
   clicks; an explicit nil keeps the row collapsed."
  [state tool]
  (assoc state ::active-tool
         (when (not= tool (::active-tool state :shift))
           tool)))

(defn reorder-rows
  "Moves the dragged row to the display position `to-idx` and reassigns
   the scheduled dates positionally: every display position keeps its
   date, so the time slots of the schedule stay while the stories are
   rearranged. The result is stored in ascending date order — the
   unsorted table view shows `::rows` as stored, and its date order
   would stay broken after a drag in the descending-sorted view
   otherwise."
  [state dragged-eid to-idx]
  (edit-rows state
             (fn [rows]
               (let [display (vec (sorted-rows rows (::sort-date state)))
                     from-idx (display-index-of display dragged-eid)
                     to-idx (when to-idx
                              (max 0
                                   (min to-idx
                                        (dec (count display)))))]
                 (if (or (nil? from-idx)
                         (nil? to-idx)
                         (= from-idx to-idx))
                   rows
                   (let [slot-dates (mapv :story.post/date-str display)]
                     (rows-in-date-order
                       (mapv (fn [row date-str]
                               (assoc row :story.post/date-str date-str))
                             (move-row display from-idx to-idx)
                             slot-dates))))))))

(defn- drop-dragged
  "Applies the drop of the dragged row on the current drop target and
   clears the drag state. The drop target is the display slot tracked
   in the store (`::drag-over-idx`), so the drop also works while the
   cursor is over the gap that the preview animation opened at the
   target position."
  [state]
  (let [dragging-eid (::dragging-eid state)
        to-idx (::drag-over-idx state)]
    (-> (if (and dragging-eid
                 to-idx)
          (reorder-rows state dragging-eid to-idx)
          state)
        (dissoc ::dragging-eid ::drag-over-idx ::slot-tops))))

(def ^:private row-drag-data-type
  ;; tags the drags of this page, so the window-level listeners below
  ;; do not react to other drags (files from the OS, selected text):
  "application/x-storrito-bulk-row")

(defn- row-drag?
  [e]
  (some #(= % row-drag-data-type)
        (array-seq (.. e -dataTransfer -types))))

(defonce ^:private window-drag-listeners
  (atom nil))

(defn- cursor-slot
  "The display slot for the cursor position `client-y`: the index of
   the DATA row whose vertical range contains the cursor — the group
   separator rows (marked with data-separator) are skipped, so the
   slot indices keep matching the display-row indices; a cursor over
   a separator resolves to the data row below it. Clamped to the
   first/last row when the cursor is above or below the table. The
   real row extents are measured via offsetTop/offsetHeight, which
   are layout metrics that the translate transforms of the drag
   preview do not affect, so the preview shifts cannot move the hit
   areas under the cursor and flicker the target back and forth."
  [client-y]
  (when-let [tbody (js/document.querySelector "#bulk-schedule-table tbody")]
    (let [data-rows (->> (array-seq (.-children tbody))
                         (remove #(.getAttribute % "data-separator")))]
      (when (seq data-rows)
        ;; tr offsets are relative to the table element, which is
        ;; itself untransformed:
        (let [table (.-parentElement tbody)
              y (- client-y
                   (.-top (.getBoundingClientRect table)))]
          (or (first (keep-indexed
                       (fn [idx el]
                         (when (< y (+ (.-offsetTop el)
                                       (.-offsetHeight el)))
                           idx))
                       data-rows))
              (dec (count data-rows))))))))

(defn- add-window-drag-listeners!
  "Reacts to the drag events on the whole window (not only on the
   table): the drop target slot follows the cursor everywhere and the
   previewed drop applies wherever the row is released."
  [dispatch!]
  (when-not @window-drag-listeners
    (let [last-slot (atom nil)
          dragover-listener (fn [e]
                              (when (row-drag? e)
                                (.preventDefault e)
                                (when-let [slot (cursor-slot (.-clientY e))]
                                  ;; only dispatch slot changes:
                                  ;; dragover fires continuously
                                  ;; during a drag:
                                  (when (not= slot @last-slot)
                                    (reset! last-slot slot)
                                    (dispatch! {:actions [[:store/assoc ::drag-over-idx slot]]})))))
          drop-listener (fn [e]
                          (when (row-drag? e)
                            (.preventDefault e)
                            (reset! last-slot nil)
                            ;; the drop position decides the target
                            ;; slot: dragover events are throttled by
                            ;; the browser, so on a fast release the
                            ;; last dragover can lag one slot behind
                            ;; the cursor (or none was processed at
                            ;; all) and the row would not land where
                            ;; it was dropped:
                            (dispatch! {:actions
                                        (if-let [slot (cursor-slot (.-clientY e))]
                                          [[:store/assoc ::drag-over-idx slot]
                                           [:store/update drop-dragged]]
                                          [[:store/update drop-dragged]])})))
          dragend-listener (fn [_e]
                             (reset! last-slot nil))
          keydown-listener (fn [e]
                             ;; Escape cancels the drag: clearing the
                             ;; drag state before the browser fires
                             ;; dragend makes the dragend apply a
                             ;; no-op, so the rows revert:
                             (when (= "Escape" (.-key e))
                               (dispatch! {:actions [[:store/dissoc ::dragging-eid]
                                                     [:store/dissoc ::drag-over-idx]
                                                     [:store/dissoc ::slot-tops]]})))]
      (js/window.addEventListener "dragover" dragover-listener)
      (js/window.addEventListener "drop" drop-listener)
      (js/window.addEventListener "dragend" dragend-listener)
      (js/window.addEventListener "keydown" keydown-listener)
      (reset! window-drag-listeners
              {:dragover dragover-listener
               :drop drop-listener
               :dragend dragend-listener
               :keydown keydown-listener}))))

(defn- remove-window-drag-listeners!
  []
  (when-let [{:keys [dragover drop dragend keydown]} @window-drag-listeners]
    (js/window.removeEventListener "dragover" dragover)
    (js/window.removeEventListener "drop" drop)
    (js/window.removeEventListener "dragend" dragend)
    (js/window.removeEventListener "keydown" keydown)
    (reset! window-drag-listeners nil)))

(defn row-shift-px
  "The vertical pixel shift of the data row at display position `idx`
   that previews the drop: the rows between the dragged row and the
   drop target move one slot aside, while the dragged row slides to
   the target slot. `slot-tops` are the slot pixel positions captured
   at dragstart (see :bulk/capture-slot-tops), so the rows slide
   exactly into the static slots — including across the group
   separator rows, which do not move: the slot dates, and with them
   the groups, keep their positions (see `reorder-rows`)."
  [slot-tops from-idx to-idx idx]
  (let [top (fn [i]
              (nth slot-tops i nil))]
    (cond
      (or (nil? from-idx)
          (nil? to-idx)
          (= from-idx to-idx)
          (nil? (top idx))
          (nil? (top from-idx))
          (nil? (top to-idx)))
      0

      (= idx from-idx)
      (- (top to-idx) (top from-idx))

      (and (< from-idx to-idx)
           (< from-idx idx)
           (<= idx to-idx))
      (- (top (dec idx)) (top idx))

      (and (< to-idx from-idx)
           (<= to-idx idx)
           (< idx from-idx))
      (- (top (inc idx)) (top idx))

      :else 0)))

(def ^:private grip-svg
  [:svg {:xmlns "http://www.w3.org/2000/svg"
         :viewBox "0 0 20 20"
         :fill "currentColor"
         :class ["h-4" "w-4" "pointer-events-none"]}
   [:circle {:cx "7" :cy "5" :r "1.5"}]
   [:circle {:cx "13" :cy "5" :r "1.5"}]
   [:circle {:cx "7" :cy "10" :r "1.5"}]
   [:circle {:cx "13" :cy "10" :r "1.5"}]
   [:circle {:cx "7" :cy "15" :r "1.5"}]
   [:circle {:cx "13" :cy "15" :r "1.5"}]])

(defn loaded-rows-by-eid
  [loaded-rows]
  (when loaded-rows
    (into {}
          (map (juxt :story.post/eid identity))
          loaded-rows)))

(defn row-status
  "The pending save state of a row compared to its loaded snapshot:
   :new rows will be created on save, :edited rows will be
   re-scheduled (cancel + add, see `prepare-ops`) and :existing rows
   are untouched and produce no save ops. Without an edit session
   (`loaded-by-eid` nil) every row is :existing."
  [loaded-by-eid row]
  (let [loaded-row (get loaded-by-eid (:story.post/eid row))]
    (cond
      (nil? loaded-by-eid) :existing
      (nil? loaded-row) :new
      (rows-differ? row loaded-row) :edited
      :else :existing)))

(def ^:private row-status-accent
  ;; colored leading edge of a table row: emerald marks rows that will
  ;; be created on save, amber rows that will be re-scheduled. The
  ;; transparent border keeps untouched rows aligned.
  {:new "border-l-emerald-500"
   :edited "border-l-amber-400"
   :existing "border-l-transparent"})

(defn- row-status-chip
  "The chip is always rendered and hidden via class for :existing
   rows: removing the element (nil child) left stale 'Edited' chips
   behind when the keyed rows were reordered back in the same render
   (dragging a row down and back up)."
  [status]
  (let [[state-classes label] (case status
                                :new [["text-emerald-700" "bg-emerald-50"] "New"]
                                :edited [["text-amber-700" "bg-amber-50"] "Edited"]
                                [["hidden"] ""])]
    [:span {:class (into ["text-[10px]" "font-semibold" "uppercase"
                          "tracking-wide" "rounded" "px-1" "py-px"
                          "shrink-0"]
                         state-classes)}
     label]))

(defn pending-changes
  "Counts what Save will do: :new rows to create, :edited rows to
   re-schedule and :removed rows to cancel."
  [rows loaded-rows]
  (let [loaded (loaded-rows-by-eid loaded-rows)
        current-eids (into #{}
                           (map :story.post/eid)
                           rows)]
    {:new (count (remove #(contains? loaded (:story.post/eid %))
                         rows))
     :edited (count (filter #(when-let [loaded-row (get loaded (:story.post/eid %))]
                               (rows-differ? % loaded-row))
                            rows))
     :removed (count (remove #(contains? current-eids (:story.post/eid %))
                             loaded-rows))}))

(def burst-gap-minutes
  "Stories at most this many minutes apart count as one burst — a
   story session posted together (e.g. the 1-minute stagger that 'Add
   from gallery' creates)."
  30)

(defn row-boundary
  "The grouping boundary between a display row and the row above it
   (`prev-row`), present when the gap to the previous story exceeds
   `burst-gap-minutes`: labeled :day when the calendar day also
   changed, :burst otherwise. The day change alone is NOT a boundary,
   so a story line posted around midnight stays one group. Nil inside
   a group, for the first row and for unparseable dates. The gap uses
   the absolute difference, so the boundaries also hold in the
   descending sort."
  [prev-row row]
  (let [prev-str (:story.post/date-str prev-row)
        cur-str (:story.post/date-str row)
        prev-date (parse-date-str prev-str)
        cur-date (parse-date-str cur-str)]
    (when (and prev-date cur-date)
      (let [gap-minutes (js/Math.round
                          (/ (js/Math.abs (- (.getTime cur-date)
                                             (.getTime prev-date)))
                             60000))]
        (when (> gap-minutes burst-gap-minutes)
          {:boundary/kind (if (not= (subs prev-str 0 10)
                                    (subs cur-str 0 10))
                            :day
                            :burst)
           :boundary/gap-minutes gap-minutes})))))

(defn first-row-boundary
  "A synthetic :day boundary for the first display row: it has no
   previous row, but its day label should still show (and act as the
   group's click target) like every other day's first row. Nil when
   the date does not parse."
  [row]
  (when (parse-date-str (:story.post/date-str row))
    {:boundary/kind :day}))

(defn group-eids
  "The eids of the display-order group starting at index `idx`: the
   row itself and every following row until the next boundary (see
   `row-boundary`)."
  [display-rows idx]
  (let [start (nth display-rows idx)]
    (loop [eids [(:story.post/eid start)]
           prev start
           [row & more] (drop (inc idx) display-rows)]
      (if (and row
               (nil? (row-boundary prev row)))
        (recur (conj eids (:story.post/eid row)) row more)
        eids))))

(defn toggle-group-selection
  "Selects the display-order group that starts at the row with `eid`
   — or deselects it when every row of the group is already selected.
   Additive, so several groups can be combined; other selected rows
   stay selected."
  [state eid]
  (let [display (vec (sorted-rows (current-rows state)
                                  (::sort-date state)))
        idx (display-index-of display eid)]
    (if (nil? idx)
      state
      (let [eids (group-eids display idx)
            selected (or (::selected-eids state) #{})]
        (assoc state ::selected-eids
               (if (every? selected eids)
                 (apply disj selected eids)
                 (into selected eids)))))))

(defn format-gap
  "Formats a gap in minutes as a short label: '45 min', '2 h',
   '2 h 30 min'."
  [minutes]
  (let [h (quot minutes 60)
        m (rem minutes 60)]
    (cond
      (zero? h) (str m " min")
      (zero? m) (str h " h")
      :else (str h " h " m " min"))))

(defn- day-label
  [date-str]
  (when-let [date (parse-date-str date-str)]
    (.toLocaleDateString date "en-US"
                         #js {:weekday "short"
                              :month "short"
                              :day "numeric"})))

(defn- group-select-checkbox
  "Tri-state checkbox that toggles the selection of the whole group
   starting at `row`: checked when every story of the group is
   selected, indeterminate when only some are. Clicking an
   indeterminate or unchecked box selects the group, a checked one
   deselects it (the `toggle-group-selection` semantics)."
  [state row]
  (let [display (vec (sorted-rows (current-rows state)
                                  (::sort-date state)))
        idx (display-index-of display (:story.post/eid row))
        eids (when idx
               (group-eids display idx))
        selected (or (::selected-eids state) #{})
        all? (and (seq eids)
                  (every? selected eids))
        mixed? (and (boolean (some selected eids))
                    (not all?))]
    (when (seq eids)
      [:input {:type "checkbox"
               :checked all?
               :aria-label "Select this group of stories"
               :title "Select this group of stories"
               :class ["h-3.5" "w-3.5" "cursor-pointer"]
               ;; forces a node update whenever the mixed state
               ;; changes, so the on-render hook below runs again:
               :data-indeterminate (str mixed?)
               ;; the indeterminate state can only be set via the DOM
               ;; property, not via an attribute:
               :replicant/on-render (fn [{:keys [replicant/node]}]
                                      (set! (.-indeterminate node)
                                            mixed?))
               :on {:click [[:store/update toggle-group-selection
                             (:story.post/eid row)]]}}])))

(defn- group-separator-row
  "Separator row between the groups: the tri-state group checkbox and
   the day (:day boundary) or gap (:burst boundary) label. Clicking
   the label toggles the group like the checkbox does (it is one
   <label>). A separate <tr>, so it stays put while the group's first
   story is dragged; the data-separator attribute lets `cursor-slot`
   skip it when mapping the cursor to a data-row slot, and the own
   key space (\"sep-<idx>\") means boundary changes only insert or
   remove keyed nodes — never move them, which the pinned replicant
   version does not handle (see the comment in `table-row`)."
  [state boundary row idx]
  [:tr {:replicant/key (str "sep-" idx)
        :data-separator "true"}
   [:td {:colSpan 9
         :class ["pt-3" "pb-1" "px-3" "border-t"
                 (if (zero? idx)
                   "border-transparent"
                   "border-gray-200")]}
    [:label {:class ["flex" "items-center" "gap-1.5" "w-fit"
                     "cursor-pointer" "text-[10px]" "font-semibold"
                     "uppercase" "tracking-wide" "text-gray-400"]}
     (group-select-checkbox state row)
     (case (:boundary/kind boundary)
       :day (day-label (:story.post/date-str row))
       :burst (str "+" (format-gap (:boundary/gap-minutes boundary))))]]])

(defn- same-minute-fix-button
  "The one-click fix rendered under the same-minute error message:
   moves the row to the next free minute (see `fix-same-minute`). Nil
   for every other date error — a past date has no safe automatic fix,
   since the story would post right after saving."
  [date-error eid]
  (when (= same-minute-error date-error)
    [:button
     {:class ["block" "text-xs" "font-medium" "text-indigo-600"
              "hover:text-indigo-800" "cursor-pointer" "mt-0.5"]
      :on {:click [[:store/update fix-same-minute eid]]}}
     "Move to next free minute"]))

(defn preview-actions
  "Opens `app.bulk.preview-modal` for the row, which reads the row as it
   is edited from the page's state."
  [eid]
  [[:ui/modal-open :modals/bulk-preview
    {:story.post/eid eid
     ;; as narrow as its content, like the agenda's preview modal:
     :max-width "sm:max-w-fit"}]])

(defn pending-status
  "The `row-status` of the row in the current edit session."
  [state row]
  (if (::rows state)
    (row-status (loaded-rows-by-eid (::loaded-rows state)) row)
    :existing))

(defn- table-row
  [state row errors fb-linkages highlights idx shift-px status boundary]
  (let [eid (:story.post/eid row)
        row-errors (get errors eid)
        dragging-eid (::dragging-eid state)
        selected? (contains? (or (::selected-eids state) #{}) eid)]
    [:tr
     ;; a group's first row draws no line of its own — the separator
     ;; row above it (see `group-separator-row`) provides the group
     ;; division; rows within a group keep the hairline:
     {:class (cond-> ["hover:bg-gray-50"
                      "border-t"
                      (if boundary
                        "border-transparent"
                        "border-gray-100")]
               selected? (conj "bg-indigo-50")
               (= dragging-eid eid) (conj "opacity-50")
               ;; only transition while a drag is in flight, so the
               ;; rows snap (instead of sliding once more) when the
               ;; reorder applies on drop:
               dragging-eid (into ["transition-transform"
                                   "duration-150"
                                   "ease-out"]))
      ;; the transform is only set while a drag is in flight: a
      ;; permanent transform would make every row a stacking context,
      ;; whereby the hover preview popover of a row would paint below
      ;; its following rows:
      :style (when dragging-eid
               {:transform (str "translateY("
                                shift-px
                                "px)")})
      ;; the rows have no dragover handlers on purpose: the drop
      ;; target slot is computed from the cursor position by the
      ;; window-level listener (see `cursor-slot`), so the preview
      ;; shifts cannot re-target the drag and flicker.
      ;;
      ;; the key is the display POSITION, not the eid: a reorder must
      ;; not MOVE keyed nodes, since the pinned replicant version does
      ;; not reliably patch moved keyed children (fixed upstream in
      ;; https://github.com/cjohansen/replicant/pull/68 — remove this
      ;; workaround after upgrading). With positional keys a reorder
      ;; is a plain in-place content update of every row.
      :replicant/key idx}
     ;; Drag handle to rearrange the schedule; the left edge carries
     ;; the row-status accent:
     [:td {:class ["py-2" "pl-3" "pr-1" "border-l-[3px]"
                   (row-status-accent status)]}
      [:span {:class ["inline-flex" "cursor-grab" "text-gray-300"
                      "hover:text-gray-500" "select-none"]
              :title "Drag to rearrange the schedule"
              :draggable "true"
              :on {:dragstart [[:js/set-drag-data row-drag-data-type]
                               [:store/assoc ::dragging-eid eid]
                               ;; snapshot the slot pixel positions
                               ;; for the pixel-exact drag preview:
                               [:bulk/capture-slot-tops]]
                   ;; dragend applies the last previewed slot: when the
                   ;; drag is released outside the page (window edge,
                   ;; devtools, the chat iframe) the browser cancels
                   ;; the drop and no drop event fires — the row must
                   ;; still land where the preview gap was. After a
                   ;; real drop this is a no-op, since drop-dragged
                   ;; already cleared the drag state. Escape clears the
                   ;; state first (see the window keydown listener), so
                   ;; a cancelled drag still reverts.
                   :dragend [[:store/update drop-dragged]]}}
       grip-svg]]
     ;; Selection for the shift toolbar
     [:td {:class ["py-2" "px-1" "text-center"]}
      [:input {:type "checkbox"
               :checked selected?
               :aria-label "Select the row for shifting its post date"
               :class ["h-4" "w-4" "cursor-pointer"]
               :on {:click [[:bulk/row-select-click eid]]}}]]
     ;; Preview
     [:td {:class ["py-2" "px-3"]}
      (when (:story.media/preview-url row)
        [:div {:class ["relative" "group" "w-fit"]}
         [:img {:src (:story.media/preview-url row)
                :class ["h-16" "w-10" "object-cover" "rounded"
                        "cursor-pointer" "hover:opacity-80"
                        "transition-opacity"]
                :on {:click (preview-actions eid)}}]
         ;; larger preview beside the thumbnail while hovering it. The
         ;; fixed width on the popover div is required: an absolutely
         ;; positioned box at left-full shrink-to-fits against zero
         ;; available width, and the preflight `img {max-width: 100%}`
         ;; would collapse the image to a 0px-wide line:
         [:div {:class ["absolute" "left-full" "top-1/2" "-translate-y-1/2"
                        "ml-2" "z-30" "w-36" "hidden" "group-hover:block"
                        "pointer-events-none"]}
          [:img {:src (:story.media/preview-url row)
                 :class ["h-64" "w-full" "object-cover" "rounded-lg"
                         "shadow-xl" "border" "border-gray-200"]}]]])]
     ;; Name
     [:td {:class ["py-2" "px-3" "text-sm" "text-gray-700" "max-w-[10rem]"]}
      [:div {:class ["flex" "items-center" "gap-1.5" "min-w-0"]}
       [:span {:class ["truncate"]}
        (:story.media/name row)]
       (row-status-chip status)]]
     ;; Date
     [:td {:class ["py-2" "px-3"]}
      [:input
       {:type "datetime-local"
        :value (some-> (:story.post/date-str row) (str/replace " " "T"))
        :min (datetime-local/current-datetime-local)
        :class (into ["w-44" "px-2" "py-1" "text-sm" "border" "rounded"
                      "focus:ring-2" "focus:ring-indigo-500" "focus:border-indigo-500"]
                     (if (:date row-errors)
                       ["border-red-300" "bg-red-50"]
                       ["border-gray-300"]))
        :on {:input [[:store/update update-row-date eid :story.post/date-str
                      :event/target.value]]
             :blur [[:store/update resort-rows]]}}]
      (when (:date row-errors)
        [:div {:class ["text-xs" "text-red-500" "mt-0.5"]}
         (:date row-errors)
         (same-minute-fix-button (:date row-errors) eid)])]
     ;; Highlight
     [:td {:class ["py-2" "px-3"]}
      (if (seq highlights)
        ;; The :selected attributes are NOT redundant: relying only on
        ;; :value on the <select> renders a saved highlight as "-",
        ;; because the value is applied before the option children exist.
        [:select
         {:class ["w-full" "text-sm" "border" "border-gray-300" "rounded"
                  "px-2" "py-1" "bg-white"]
          :value (or (:story.post/highlight-id row) "")
          :on {:change [[:store/update update-row eid :story.post/highlight-id
                          :event/target.value]]}}
         [:option {:value ""
                   :selected (empty? (:story.post/highlight-id row))}
          "-"]
         (map (fn [h]
                [:option {:value (:highlight/id h)
                          :selected (= (:highlight/id h)
                                       (:story.post/highlight-id row))
                          :replicant/key (:highlight/id h)}
                 (:highlight/title h)])
              highlights)]
        [:span {:class ["text-xs" "text-gray-400"]} "-"])]
     ;; FB cross-post; shift-click applies the toggle to the whole
     ;; range since the last FB click, like the selection checkboxes:
     [:td {:class ["py-2" "px-3" "text-center"]}
      (if (seq fb-linkages)
        [:input {:type "checkbox"
                 :checked (boolean (:share-to-fb? row))
                 :class ["h-4" "w-4" "cursor-pointer"]
                 :on {:click [[:bulk/fb-click eid (first fb-linkages)]]}}]
        [:span {:class ["text-xs" "text-gray-400"]} "-"])]
     ;; AI-generated content; shift-click ranges like the FB column:
     [:td {:class ["py-2" "px-3" "text-center"]}
      [:span {:data-tip "Label the story as AI-generated on Instagram"
              :class ["tooltip" "inline-flex"]}
       [:input {:type "checkbox"
                :aria-label "Label the story as AI-generated on Instagram"
                :checked (boolean (:story.post/ai-generated? row))
                :class ["h-4" "w-4" "cursor-pointer"]
                :on {:click [[:bulk/ai-click eid]]}}]]]
     ;; Delete
     [:td {:class ["py-2" "px-3" "text-right"]}
      [:button
       {:class ["text-red-500" "hover:text-red-700" "cursor-pointer"
                "text-sm" "font-medium"]
        :on {:click [[:store/update remove-row eid]]}}
       "Remove"]]]))

(defn- select-all-checkbox
  "Checkbox for a table column header that toggles a boolean flag for
   all rows at once. Shows the indeterminate state while `flag-fn` is
   only truthy for some rows. `->click-actions` is called with the new
   boolean value and returns the click actions."
  [{:keys [rows flag-fn ->click-actions aria-label]}]
  (let [flagged (filter flag-fn rows)
        all-flagged? (= (count flagged)
                        (count rows))
        mixed? (and (seq flagged)
                    (not all-flagged?))]
    [:input {:type "checkbox"
             :checked all-flagged?
             :aria-label aria-label
             :class ["h-4" "w-4" "cursor-pointer"]
             ;; forces a node update whenever the mixed state changes,
             ;; so the on-render hook below runs again:
             :data-indeterminate (str mixed?)
             ;; the indeterminate state can only be set via the DOM
             ;; property, not via an attribute:
             :replicant/on-render (fn [{:keys [replicant/node]}]
                                    (set! (.-indeterminate node)
                                          mixed?))
             :on {:click (->click-actions (not all-flagged?))}}]))

(defn- desktop-table
  [state rows errors fb-linkages highlights loaded-rows]
  (let [sort-dir (::sort-date state)
        dragging-eid (::dragging-eid state)
        selected-set (or (::selected-eids state) #{})
        dispatch! (:replicant/dispatch! state)]
    [:div
     ;; no overflow-hidden: it would clip the hover preview popovers
     ;; of the thumbnail column. The top corners are rounded on the
     ;; corner th's instead, so the gray thead does not stick out of
     ;; the rounded border:
     {:class ["hidden" "sm:block" "bg-white" "rounded-lg" "border"
              "border-gray-200"]
      ;; the drag events are handled on the whole window (not only on
      ;; the table), so the previewed drop applies wherever the row is
      ;; released:
      :replicant/on-mount (fn [_]
                            (add-window-drag-listeners! dispatch!))
      :replicant/on-unmount (fn [_]
                              (remove-window-drag-listeners!))}
     [:table {:id "bulk-schedule-table"
              :class ["w-full" "text-sm"]}
      [:thead
       [:tr {:class ["border-b" "border-gray-200" "bg-gray-50"
                     "text-left" "text-gray-500" "text-xs"]}
        [:th {:class ["py-2" "pl-3" "pr-1" "w-8" "rounded-tl-lg"]} ""]
        [:th {:class ["py-2" "px-1" "w-8" "text-center"]}
         (when (seq rows)
           (select-all-checkbox
             {:rows rows
              :flag-fn #(contains? selected-set (:story.post/eid %))
              :->click-actions (fn [select?]
                                 [[:store/update set-all-selected select?]])
              :aria-label "Select all rows for shifting their post dates"}))]
        [:th {:class ["py-2" "px-3" "font-medium" "w-14"]} ""]
        [:th {:class ["py-2" "px-3" "font-medium"]} "Name"]
        [:th {:class ["py-2" "px-3" "font-medium" "cursor-pointer" "select-none"
                       "hover:text-gray-700"]
              :on {:click [[:store/assoc ::sort-date
                            (case sort-dir :asc :desc :desc nil :asc)]]}}
         (str "Post date" (sort-indicator sort-dir))]
        [:th {:class ["py-2" "px-3" "font-medium"]} "Highlight"]
        [:th {:class ["py-2" "px-3" "font-medium" "text-center"]}
         ;; label above the checkbox, so the checkbox sits on the
         ;; column center like the row checkboxes below it:
         [:div {:class ["flex" "flex-col" "items-center" "gap-1.5"]}
          [:span "FB"]
          (when (and (seq rows)
                     (seq fb-linkages))
            (select-all-checkbox
              {:rows rows
               :flag-fn :share-to-fb?
               :->click-actions (fn [share?]
                                  [[:store/update set-all-share-to-fb
                                    share?
                                    (first fb-linkages)]])
               :aria-label "Toggle FB cross-posting for all rows"}))]]
        [:th {:class ["py-2" "px-3" "font-medium" "text-center"]}
         [:div {:class ["flex" "flex-col" "items-center" "gap-1.5"]}
          [:span {:data-tip "Label the story as AI-generated on Instagram"
                  :class ["tooltip" "tooltip-bottom"]}
           "AI"]
          (when (seq rows)
            (select-all-checkbox
              {:rows rows
               :flag-fn :story.post/ai-generated?
               :->click-actions (fn [value]
                                  [[:store/update set-all-rows
                                    :story.post/ai-generated?
                                    value]])
               :aria-label "Toggle the AI-generated label for all rows"}))]]
        [:th {:class ["py-2" "px-3" "font-medium" "w-20" "rounded-tr-lg"]} ""]]]
      [:tbody
       (let [display-rows (vec (sorted-rows rows sort-dir))
             from-idx (display-index-of display-rows dragging-eid)
             to-idx (::drag-over-idx state)
             ;; the same reconciled `loaded-rows` the save logic uses
             ;; (see `render`), so the row markers can never disagree
             ;; with the Save button:
             loaded-by-eid (loaded-rows-by-eid loaded-rows)]
         (if (seq display-rows)
           (mapcat
             (fn [idx row]
               (let [boundary (if (pos? idx)
                                (row-boundary (nth display-rows (dec idx))
                                              row)
                                (first-row-boundary row))]
                 (concat
                   (when boundary
                     [(group-separator-row state boundary row idx)])
                   [(table-row state row errors fb-linkages highlights idx
                               (row-shift-px (::slot-tops state)
                                             from-idx to-idx idx)
                               (row-status loaded-by-eid row)
                               boundary)])))
             (range)
             display-rows)
           [:tr
            [:td {:colSpan 9
                  :class ["py-12" "text-center" "text-gray-400"]}
             "Nothing scheduled for this account."]]))]]]))

;; -- Mobile cards --

(defn- mobile-card
  [row errors fb-linkages highlights status selected?]
  (let [eid (:story.post/eid row)
        row-errors (get errors eid)]
    [:div
     {:class ["bg-white" "rounded-lg" "border" "border-gray-200" "p-3"
              "flex" "gap-3" "border-l-[3px]"
              ;; the cards keep their gray border when untouched, so
              ;; the accent does not punch a hole into the outline:
              (if (= status :existing)
                "border-l-gray-200"
                (row-status-accent status))]
      :replicant/key eid}
     (when (:story.media/preview-url row)
       [:img {:src (:story.media/preview-url row)
              :class ["h-20" "w-12" "object-cover" "rounded" "shrink-0"
                      "cursor-pointer"]
              :on {:click (preview-actions eid)}}])
     [:div
      {:class ["flex-1" "min-w-0" "space-y-2"]}
      [:div {:class ["flex" "items-center" "gap-1.5" "min-w-0"]}
       [:input {:type "checkbox"
                :checked (boolean selected?)
                :aria-label "Select the story for shifting its post date"
                :class ["h-3.5" "w-3.5" "cursor-pointer" "shrink-0"]
                :on {:click [[:bulk/row-select-click eid]]}}]
       [:span {:class ["text-sm" "font-medium" "text-gray-900" "truncate"]}
        (or (:story.media/name row) "Untitled")]
       (row-status-chip status)]
      [:input
       {:type "datetime-local"
        :value (some-> (:story.post/date-str row) (str/replace " " "T"))
        :min (datetime-local/current-datetime-local)
        :class (into ["w-full" "px-2" "py-1" "text-sm" "border" "rounded"]
                     (if (:date row-errors)
                       ["border-red-300" "bg-red-50"]
                       ["border-gray-300"]))
        :on {:input [[:store/update update-row-date eid :story.post/date-str
                      :event/target.value]]
             :blur [[:store/update resort-rows]]}}]
      (when (:date row-errors)
        [:div {:class ["text-xs" "text-red-500"]}
         (:date row-errors)
         (same-minute-fix-button (:date row-errors) eid)])
      (when (seq highlights)
        ;; :selected is required — see the comment in `table-row`.
        [:select
         {:class ["w-full" "text-xs" "border" "border-gray-300" "rounded"
                  "px-2" "py-1" "bg-white"]
          :value (or (:story.post/highlight-id row) "")
          :on {:change [[:store/update update-row eid :story.post/highlight-id
                          :event/target.value]]}}
         [:option {:value ""
                   :selected (empty? (:story.post/highlight-id row))}
          "No highlight"]
         (map (fn [h]
                [:option {:value (:highlight/id h)
                          :selected (= (:highlight/id h)
                                       (:story.post/highlight-id row))
                          :replicant/key (:highlight/id h)}
                 (:highlight/title h)])
              highlights)])
      [:div {:class ["flex" "items-center" "justify-between"]}
       [:div {:class ["flex" "items-center" "gap-3"]}
        (when (seq fb-linkages)
          [:label {:class ["flex" "items-center" "gap-1.5" "text-xs" "text-gray-500"]}
           [:input {:type "checkbox"
                    :checked (boolean (:share-to-fb? row))
                    :class ["h-3.5" "w-3.5" "cursor-pointer"]
                    :on {:click [[:bulk/fb-click eid (first fb-linkages)]]}}]
           "FB"])
        [:label {:class ["tooltip" "flex" "items-center" "gap-1.5" "text-xs"
                         "text-gray-500"]
                 :data-tip "Label the story as AI-generated on Instagram"}
         [:input {:type "checkbox"
                  :checked (boolean (:story.post/ai-generated? row))
                  :class ["h-3.5" "w-3.5" "cursor-pointer"]
                  :on {:click [[:bulk/ai-click eid]]}}]
         "AI"]]
       [:button
        {:class ["text-xs" "text-red-500" "font-medium" "cursor-pointer"]
         :on {:click [[:store/update remove-row eid]]}}
        "Remove"]]]]))

(defn- mobile-cards
  [state rows errors fb-linkages highlights loaded-rows]
  [:div
   {:class ["sm:hidden" "space-y-3"]}
   (if (seq rows)
     (let [loaded-by-eid (loaded-rows-by-eid loaded-rows)
           selected-set (or (::selected-eids state) #{})
           ;; the same display order as the desktop table and the
           ;; shift-click range selection (`row-select-click`), so a
           ;; range never selects rows outside the visible span:
           display-rows (vec (sorted-rows rows (::sort-date state)))]
       ;; day headings between the cards; bursts get no extra marker
       ;; on mobile, the cards are already visually separate:
       (mapcat (fn [prev-row row]
                 (concat
                   ;; a heading between the cards at every group
                   ;; start (the first card gets one via the
                   ;; synthetic first boundary), with the tri-state
                   ;; checkbox toggling the group's selection:
                   (when-let [boundary (if prev-row
                                         (row-boundary prev-row row)
                                         (first-row-boundary row))]
                     [[:label
                       {:class ["flex" "items-center" "gap-1.5" "w-fit"
                                "pt-1" "text-[10px]" "font-semibold"
                                "uppercase" "tracking-wide" "text-gray-400"
                                "cursor-pointer"]
                        :replicant/key (str "group-" (:story.post/eid row))}
                       (group-select-checkbox state row)
                       (case (:boundary/kind boundary)
                         :day (day-label (:story.post/date-str row))
                         :burst (str "+" (format-gap (:boundary/gap-minutes boundary))))]])
                   [(mobile-card row errors fb-linkages highlights
                                 (row-status loaded-by-eid row)
                                 (contains? selected-set (:story.post/eid row)))]))
               (cons nil display-rows)
               display-rows))
     [:div {:class ["py-12" "text-center" "text-gray-400" "bg-white"
                    "rounded-lg" "border" "border-gray-200"]}
      "Nothing scheduled for this account."])])

;; -- Header --

(defn- pending-changes-summary
  "Quiet legend next to Save that says what the save will do, in the
   same colors as the row markers. Removed rows only appear here,
   since they are no longer visible in the table."
  [rows loaded-rows]
  (let [counts (pending-changes rows loaded-rows)
        legend-item (fn [n dot-class label]
                      (when (pos? n)
                        [:span {:class ["inline-flex" "items-center" "gap-1.5"
                                        "whitespace-nowrap"]}
                         [:span {:class ["w-2" "h-2" "rounded-full" dot-class]}]
                         (str n " " label)]))]
    (when (some pos? (vals counts))
      [:div {:class ["flex" "items-center" "gap-3" "text-xs" "text-gray-500"]}
       (legend-item (:new counts) "bg-emerald-500" "new")
       (legend-item (:edited counts) "bg-amber-400" "edited")
       (legend-item (:removed counts) "bg-gray-400" "removed")])))

(defn- header-bar
  [state result rows loaded-rows]
  (let [has-changes (unsaved-changes? rows loaded-rows)
        errors (validate-rows rows loaded-rows)
        no-errors (empty? errors)
        can-save (and has-changes no-errors
                      (or (:organization/positive-credit-balance? result)
                          (:organization/legacy-customer? result)))
        cmd (save-command rows loaded-rows)
        saving? (::saving? state)
        save-status (::save-status state)]
    [:div
     {:class ["flex" "items-center" "gap-2" "flex-wrap"]}

     (when (::selected-ig-user-id state)
       [:button
        {:class ["px-3" "py-1.5" "text-sm" "font-medium" "rounded-lg"
                 "bg-indigo-600" "text-white" "hover:bg-indigo-700"
                 "cursor-pointer" "inline-flex" "items-center" "gap-1.5"]
         :on {:click [[:ui/modal-open :modals/bulk-gallery {}]]}}
        [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 20 20"
               :fill "currentColor" :class ["w-4" "h-4"]}
         [:path {:d "M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z"}]]
        "Add from gallery"])

     (when (seq errors)
       [:span {:class ["text-xs" "text-red-600" "bg-red-50" "px-2" "py-1"
                        "rounded-full" "font-medium"]}
        "Please fix errors"])

     [:div {:class ["flex-grow"]}]

     (when has-changes
       (pending-changes-summary rows loaded-rows))

     (when (and (not has-changes)
                (= :success save-status))
       [:span {:class ["text-xs" "text-green-600" "font-medium"]}
        "Saved!"])

     (when (= :error save-status)
       [:span {:class ["text-xs" "text-red-600" "font-medium"]}
        "Save failed"])

     (when has-changes
       [:button
        {:class ["px-3" "py-1.5" "text-sm" "font-medium" "rounded-lg"
                 "border" "border-gray-300" "text-gray-700" "bg-white"
                 "hover:bg-gray-50" "cursor-pointer"]
         :on {:click [[:store/assoc ::rows nil]
                      [:store/assoc ::loaded-rows nil]
                      [:store/dissoc ::saving?]
                      [:store/dissoc ::save-status]
                      [:store/update clear-selection]]}}
        "Discard"])

     (when has-changes
       [:button
        {:class (into ["px-3" "py-1.5" "text-sm" "font-semibold" "rounded-lg"
                       "cursor-pointer"]
                      (if can-save
                        ["bg-indigo-600" "text-white" "hover:bg-indigo-700"]
                        ["bg-gray-300" "text-gray-500" "cursor-not-allowed"]))
         :disabled (or (not can-save)
                       saving?)
         :on {:click (when can-save
                       [[:store/assoc ::saving? true]
                        [:store/dissoc ::save-status]
                        [:data/command
                         cmd
                         ;; `:bulk/save-succeeded` keeps `::rows` visible
                         ;; until the refreshed query result arrives.
                         ;; Clearing the rows right away would render the
                         ;; stale pre-save query result for a moment, so
                         ;; a just-saved highlight would flash back to
                         ;; "-" after every save.
                         {:on-success [[:bulk/save-succeeded]]
                          :on-error [[:store/assoc ::saving? false]
                                     [:store/assoc ::save-status :error]
                                     [:ui/show-error]]}]])}}
        (if saving? "Saving..." "Save")])]))

(defn- set-shift-unit
  [state value]
  (assoc state ::shift-unit (keyword value)))

(def ^:private slide-open-style
  ;; a grid animating its single row track from 0fr to 1fr slides the
  ;; content open and pushes what follows down smoothly instead of
  ;; jumping; the direct child needs min-height 0 + overflow hidden
  ;; for the track to collapse. :replicant/unmounting keeps the node
  ;; until the transition ends, so it also slides shut:
  {:style {:display "grid"
           :grid-template-rows "1fr"
           :opacity 1
           :transition (str "grid-template-rows 200ms ease-out, "
                            "opacity 200ms ease-out")}
   :replicant/mounting {:style {:grid-template-rows "0fr"
                                :opacity 0}}
   :replicant/unmounting {:style {:grid-template-rows "0fr"
                                  :opacity 0}}})

(def ^:private chevron-svg
  [:svg {:xmlns "http://www.w3.org/2000/svg"
         :viewBox "0 0 20 20"
         :fill "currentColor"
         :class ["h-3" "w-3" "pointer-events-none"]}
   [:path {:fill-rule "evenodd"
           :clip-rule "evenodd"
           :d "M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"}]])

(defn- shift-panel
  "Second toolbar row with the controls of the 'Shift by' tool. The
   Earlier/Later buttons apply the shift directly (no separate Apply):
   repeated clicks nudge the selection further, the opposite button
   undoes a wrong shift."
  [state]
  (let [unit (::shift-unit state :minutes)
        offset (shift-offset state)
        ;; like the Save button covers ::saving?: while the post-save
        ;; refresh is in flight, edit-rows would swallow the shift
        ;; silently, so the buttons must not look clickable:
        can-shift? (and offset
                        (not (::awaiting-refresh? state)))
        shift-button
        (fn [direction label]
          [:button
           {:class (into ["px-3" "py-1.5" "text-sm" "font-medium"
                          "rounded-lg" "border"]
                         (if can-shift?
                           ["border-indigo-600" "text-indigo-600"
                            "hover:bg-indigo-50" "cursor-pointer"]
                           ["border-gray-300" "text-gray-400"
                            "cursor-not-allowed"]))
            :disabled (not can-shift?)
            :on {:click (when can-shift?
                          [[:store/update apply-shift direction]])}}
           label])]
    [:div {:class ["flex" "items-center" "gap-2" "flex-wrap"
                   "pt-2" "mt-2" "border-t" "border-gray-100"]}
     [:input {:type "number"
              :min "1"
              :max "9999"
              :value (::shift-amount state "30")
              :aria-label "Shift amount"
              ;; the red border explains the disabled buttons when the
              ;; amount does not parse (empty, decimal, > 9999):
              :class (into ["w-20" "px-2" "py-1" "text-sm" "border"
                            "rounded" "focus:ring-2"
                            "focus:ring-indigo-500"
                            "focus:border-indigo-500"]
                           (if offset
                             ["border-gray-300"]
                             ["border-red-300" "bg-red-50"]))
              :on {:input [[:store/assoc ::shift-amount
                            :event/target.value]]}}]
     ;; :selected is required — see the comment in `table-row`.
     ;; pl-2/pr-8 instead of px-2: the wider right padding keeps the
     ;; dropdown arrow from overlapping the longest option label
     ;; ("minutes"):
     [:select {:class ["text-sm" "border" "border-gray-300" "rounded"
                       "pl-2" "pr-8" "py-1" "bg-white"]
               :value (name unit)
               :aria-label "Shift unit"
               :on {:change [[:store/update set-shift-unit
                              :event/target.value]]}}
      (map (fn [u]
             [:option {:value (name u)
                       :selected (= u unit)
                       :replicant/key (name u)}
              (name u)])
           [:minutes :hours :days :months])]
     (shift-button :earlier "← Earlier")
     (shift-button :later "Later →")]))

(defn- move-panel
  "Second toolbar row with the controls of the 'Move to' tool: the
   earliest selected story is moved onto the chosen datetime, the
   other selected stories follow keeping their spacing."
  [state]
  (let [;; the picker defaults to the anchor of the move — the
        ;; earliest selected story — so the user adjusts the
        ;; sequence's current start instead of typing from scratch:
        value (or (::move-to state)
                  (some-> (earliest-selected-date-str state)
                          (str/replace " " "T"))
                  "")
        target-valid? (some? (parse-date-str
                               (str/replace value "T" " ")))
        can-move? (and target-valid?
                       (not (::awaiting-refresh? state)))]
    [:div {:class ["flex" "items-center" "gap-2" "flex-wrap"
                   "pt-2" "mt-2" "border-t" "border-gray-100"]}
     [:span {:class ["text-sm" "text-gray-500"]} "Move first story to"]
     [:input {:type "datetime-local"
              :value value
              :min (datetime-local/current-datetime-local)
              :aria-label "Move the earliest selected story to"
              :class ["w-44" "px-2" "py-1" "text-sm" "border" "rounded"
                      "border-gray-300" "focus:ring-2"
                      "focus:ring-indigo-500" "focus:border-indigo-500"]
              :on {:input [[:store/assoc ::move-to
                            :event/target.value]]}}]
     [:button
      {:class (into ["px-3" "py-1.5" "text-sm" "font-semibold"
                     "rounded-lg" "cursor-pointer"]
                    (if can-move?
                      ["bg-indigo-600" "text-white" "hover:bg-indigo-700"]
                      ["bg-gray-300" "text-gray-500" "cursor-not-allowed"]))
       :disabled (not can-move?)
       :on {:click (when can-move?
                     [[:store/update apply-move-to]])}}
      "Move"]
     [:span {:class ["text-xs" "text-gray-400"]}
      "The other selected stories follow, keeping their spacing."]]))

(defn- selection-toolbar
  "Toolbar over the table while rows are selected: the selection
   context, the tool picker ('Shift by' / 'Move to'), the one-shot
   Remove action, and the active tool's controls in a second row that
   slides open below the picker."
  [state rows]
  (let [selected (selected-in-rows (::selected-eids state) rows)]
    (when (seq selected)
      (let [active-tool (::active-tool state :shift)
            tool-button
            (fn [tool label]
              (let [active? (= tool active-tool)]
                [:button
                 {:class (into ["px-3" "py-1.5" "text-xs" "font-medium"
                                "inline-flex" "items-center" "gap-1"
                                "cursor-pointer"]
                               (if active?
                                 ["bg-indigo-600" "text-white"]
                                 ["bg-white" "text-gray-700"
                                  "hover:bg-gray-50"]))
                  :aria-pressed active?
                  :on {:click [[:store/update toggle-tool tool]]}}
                 label
                 [:span {:class (cond-> ["inline-flex" "transition-transform"
                                         "duration-150"]
                                  active? (conj "rotate-180"))}
                  chevron-svg]]))]
        [:div slide-open-style
         [:div {:class ["min-h-0" "overflow-hidden"]}
          [:div {:class ["bg-white" "rounded-lg" "border" "border-gray-200"
                         "px-3" "py-2"]}
           ;; row 1: selection context | tool picker …… one-shot actions
           [:div {:class ["flex" "items-center" "gap-3" "flex-wrap"]}
            [:span {:class ["text-sm" "font-medium" "text-gray-700"
                            "whitespace-nowrap"]}
             (str (count selected) " selected")]
            [:button
             {:class ["text-sm" "text-gray-400" "hover:text-gray-600"
                      "cursor-pointer"]
              :on {:click [[:store/update clear-selection]]}}
             "Clear"]
            [:div {:class ["h-5" "w-px" "bg-gray-200"]}]
            [:div {:class ["flex" "rounded-lg" "border" "border-gray-300"
                           "overflow-hidden"]}
             (tool-button :shift "Shift by")
             (tool-button :move "Move to")]
            ;; pushes the one-shot actions to the far edge, so the
            ;; wide bar reads balanced instead of left-clustered:
            [:div {:class ["flex-grow"]}]
            ;; not destructive: removed rows are only cancelled on
            ;; Save, and Discard restores them:
            [:button
             {:class ["text-sm" "font-medium" "text-red-500"
                      "hover:text-red-700" "cursor-pointer"]
              :on {:click [[:store/update remove-selected-rows]]}}
             "Remove"]]
           ;; row 2: the active tool's controls
           (when-let [panel (case active-tool
                              :shift (shift-panel state)
                              :move (move-panel state)
                              nil)]
             [:div slide-open-style
              [:div {:class ["min-h-0" "overflow-hidden"]}
               panel]])]]]))))

;; -- Main render --

(defn- selected-ig-user-id
  "The id of the selected Instagram user, else of the first one."
  [state users]
  (or (::selected-ig-user-id state)
      (:instagram.user/id (first users))))

(defn- user-by-id
  [users id]
  (some #(when (= id (:instagram.user/id %)) %) users))

(defn selected-user
  "The selected Instagram user of the page, else the first one."
  [state]
  (let [users (:instagram/users (query/get-result state (init-query state)))]
    (user-by-id users (selected-ig-user-id state users))))

(defn render
  [state]
  (let [;; Initial query (no user) to get user list
        init-q (init-query state)
        init-result (query/get-result state init-q)
        ;; User-specific query for posts
        q (bulk-query state)
        user-result (when (::selected-ig-user-id state)
                      (query/get-result state q))
        ;; Merge: users from init, posts from user-specific
        result (merge init-result user-result)
        users (:instagram/users result)
        rows (current-rows state)
        ;; `::rows` and `::loaded-rows` are set together by
        ;; `start-editing`: when edits exist, diff them against the
        ;; snapshot they started from — never against a query result
        ;; that may have refreshed in the meantime.
        loaded-rows (if (::rows state)
                      (::loaded-rows state)
                      (:bulk/posts result))
        loading (and (query/loading? state init-q)
                     (not (query/available? state init-q)))
        errors (when rows (validate-rows rows loaded-rows))
        selected-id (selected-ig-user-id state users)
        selected (user-by-id users selected-id)
        fb-linkages (:instagram.user/facebook-linkages selected)
        highlights (:bulk/highlights result)]
    ;; Auto-select first IG user when none is selected and users are loaded
    (when (and (nil? (::selected-ig-user-id state))
               (seq users))
      (let [first-id (:instagram.user/id (first users))
            dispatch! (:replicant/dispatch! state)]
        (js/requestAnimationFrame
          (fn []
            (dispatch!
              {:actions [[:store/assoc ::selected-ig-user-id first-id]
                         [:ig/save-selection first-id]
                         [:data/query {:query/kind :query/bulk
                                       :query/data {:instagram.user/id first-id
                                                    :timezone-offset-ms (::timezone-offset-ms state)
                                                    :time-zone-id (::time-zone-id state)}}]]})))))
    ;; The post-save refresh has landed: drop the kept rows so the
    ;; table renders the fresh server data (same values, new eids).
    ;; The handler re-checks the state, so a stray extra dispatch
    ;; is harmless.
    (when (and (::awaiting-refresh? state)
               (not (query/loading? state q)))
      (let [dispatch! (:replicant/dispatch! state)]
        (js/requestAnimationFrame
          (fn []
            (dispatch! {:actions [[:bulk/finish-refresh]]})))))
    (if loading
      [:div
       {:class ["flex" "items-center" "justify-center" "min-h-screen"]}
       [:div {:class ["text-gray-500"]} "Loading..."]]
      (app-frame/app-frame
        state
        [:div
         {:class ["space-y-4"]}
         [:div
          {:class ["flex" "items-center" "gap-3"]}
          (if (seq users)
            (account-selector/render
              {:state state
               :users users
               :selected-id selected-id
               :group-labels {:instagram "Instagram story"}
               :click-actions-fn (fn [user-id]
                                   (ig-user-click-actions state user-id))})
            [:div {:class ["flex-grow"]}])]
         (account-selector/reconnect-banner selected)
         (header-bar state result rows loaded-rows)
         (selection-toolbar state rows)
         (desktop-table state rows errors fb-linkages highlights loaded-rows)
         (mobile-cards state rows errors fb-linkages highlights loaded-rows)]))))

(defn on-load
  [_location]
  (let [tz-offset (* -1 60 1000 (.getTimezoneOffset (js/Date.)))
        ;; lets the server format each post with the offset of its own
        ;; day across a DST change (see `app.bulk.query/query-time-zone`):
        time-zone-id (.-timeZone (.resolvedOptions (js/Intl.DateTimeFormat.)))
        ig-user-id (:instagram.user/id @local-storage/state)]
    (cond-> [[:ui/hide-sidebar-on-mobile]
             [:store/assoc ::timezone-offset-ms tz-offset]
             [:store/assoc ::time-zone-id time-zone-id]
             [:data/query {:query/kind :query/bulk
                           :query/data {:timezone-offset-ms tz-offset
                                        :time-zone-id time-zone-id}}]]
      ig-user-id
      (into [[:store/assoc ::selected-ig-user-id ig-user-id]
             [:data/query {:query/kind :query/bulk
                           :query/data {:instagram.user/id ig-user-id
                                        :timezone-offset-ms tz-offset
                                        :time-zone-id time-zone-id}}]]))))

(def page
  {:page-id :pages/bulk
   :route [["ui" "bulk"]]
   :on-load on-load
   :render #'render})
